<template>
  <dialog-base
    :visible.sync="visible"
    title="Chi tiết phiếu thu trước khi thay đổi"
    size="xl"
    :show-confirm="false"
    cancel-text="Đóng"
    cancel-type="danger"
    :use-panel="false"
    :append-to-body="true"
    top="5vh"
    @cancel="handleClose"
  >
    <div v-loading="isLoading">
      <!-- Thông tin chung -->
      <div class="sk-panel mb-4" v-if="detailData">
        <div class="sk-panel-head">
          <div class="sk-panel-icon sk-panel-icon--success">
            <i class="el-icon-document" />
          </div>
          <div>
            <p class="sk-panel-title">Mã phiếu thu: {{ detailData.invoiceCode || '—' }}</p>
            <p class="sk-panel-subtitle">
              Người thao tác: <strong>{{ modifiedByName }}</strong>
              &mdash; Thời gian: <strong>{{ modifiedDate | formatDateTime }}</strong>
              &mdash; Hành động: <strong>{{ actionLabel }}</strong>
            </p>
          </div>
        </div>
      </div>

      <!-- Tóm tắt số tiền -->
      <el-row :gutter="20" class="mb-4" v-if="snapshot">
        <el-col :span="6">
          <div class="sk-panel sk-panel--flat" style="padding: 12px; text-align: center; background-color: #eff6ff; border-color: #bfdbfe;">
            <div style="color: #1e3a8a; font-size: 13px; margin-bottom: 4px; font-weight: 600;">Phải thu</div>
            <div class="tdi-money--bold" style="font-size: 17px; color: #1e40af;">{{ snapshot.sumMoney | formatCurrencyLocal }}</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="sk-panel sk-panel--flat" style="padding: 12px; text-align: center; background-color: #f0fdf4; border-color: #bbf7d0;">
            <div style="color: #14532d; font-size: 13px; margin-bottom: 4px; font-weight: 600;">Đã thu</div>
            <div style="font-size: 17px; font-weight: 700; color: #166534;">{{ snapshot.collectedMoney | formatCurrencyLocal }}</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="sk-panel sk-panel--flat" style="padding: 12px; text-align: center; background-color: #fffbeb; border-color: #fde68a;">
            <div style="color: #78350f; font-size: 13px; margin-bottom: 4px; font-weight: 600;">Thiếu/Thừa phiếu thu trước</div>
            <div :class="(snapshot.beforeMoney || 0) < 0 ? 'tdi-money--negative' : 'tdi-money--positive'" style="font-size: 17px; font-weight: 700;">
              {{ snapshot.beforeMoney | formatCurrencyLocal }}
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="sk-panel sk-panel--flat" style="padding: 12px; text-align: center; background-color: #fef2f2; border-color: #fecaca;">
            <div style="color: #7f1d1d; font-size: 13px; margin-bottom: 4px; font-weight: 600;">Thiếu/Thừa còn lại</div>
            <div :class="(snapshot.afterMoney || 0) < 0 ? 'tdi-money--negative' : 'tdi-money--positive'" style="font-size: 17px; font-weight: 700;">
              {{ snapshot.afterMoney | formatCurrencyLocal }}
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- Chi tiết các khoản thu -->
      <div v-if="snapshot && snapshot.invoiceDetails && snapshot.invoiceDetails.length > 0">
        <p style="font-weight: 600; color: #1e293b; margin-bottom: 12px; font-size: 14px;">Chi tiết các khoản thu</p>
        <el-table
          :data="flattenedDetails"
          border
          class="sk-table custom-header-table"
          style="width: 100%"
          :cell-style="cellStyleMethod"
          row-key="rowKey"
        >
          <el-table-column label="STT" width="60" align="center">
            <template slot-scope="{ row }">
              <span :class="{ 'child-index': row._isChild }">{{ row._displayIndex }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Tên khoản thu" min-width="200" align="left">
            <template slot-scope="{ row }">
              <div :style="{ paddingLeft: `${(row._depth || 0) * 24}px` }" class="name-cell">
                <el-button
                  v-if="row._hasChildren"
                  type="text"
                  size="mini"
                  class="toggle-btn"
                  @click="toggleExpand(row._parentIdx)"
                >
                  <i :class="expandedMap[row._parentIdx] ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" />
                </el-button>
                <span :class="{ 'parent-name': !row._isChild, 'child-name': row._isChild }">
                  {{ row.revenueName }}
                </span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="HĐĐT" width="70" align="center">
            <template slot-scope="{ row }">
              <el-checkbox v-model="row.issuingElectronicInvoices" disabled />
            </template>
          </el-table-column>

          <el-table-column label="Đơn vị" prop="unit" width="90" align="center">
            <template slot-scope="{ row }">
              <span>{{ row.unit || '—' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Số lượng" width="90" align="center">
            <template slot-scope="{ row }">
              <span>{{ formatQuantity(row.quantity) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Đơn giá" width="140" align="right">
            <template slot-scope="{ row }">
              <span>{{ row.price | formatCurrency }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Giảm giá" width="130" align="right">
            <template slot-scope="{ row }">
              <span v-if="row.discount">
                {{ row.discountNumber | formatCurrency }}
                <span v-if="row.discountType === 'PERCENT'" class="text-muted"> (%)</span>
              </span>
              <span v-else class="text-muted">—</span>
            </template>
          </el-table-column>

          <el-table-column label="Thành tiền" width="140" align="right">
            <template slot-scope="{ row }">
              <span class="tdi-money--bold">{{ row.amount | formatCurrency }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-else-if="snapshot && (!snapshot.invoiceDetails || snapshot.invoiceDetails.length === 0)">
        <el-empty description="Không có khoản thu nào" :image-size="80"></el-empty>
      </div>
    </div>
  </dialog-base>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import ManagingStudentInvoiceService from "@/services/tutition_v3/ManagingStudentInvoiceService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import moment from "moment";
import { formatQuantity as formatQuantityValue } from "@/components/tutition_v3/utils/quantity";

export default {
  name: "InvoiceAuditDetailDialog",
  components: { DialogBase },
  data() {
    return {
      visible: false,
      isLoading: false,
      detailData: null,
      snapshot: null,
      modifiedByName: "",
      modifiedDate: "",
      actionLabel: "",
      expandedMap: {},
    };
  },
  computed: {
    flattenedDetails() {
      const result = [];
      const details = this.snapshot?.invoiceDetails || [];
      const alphabet = "abcdefghijklmnopqrstuvwxyz";

      const appendChildren = (children, parentIndex, path = [], depth = 1, displayPrefix = "") => {
        children.forEach((child, childIndex) => {
          const childPath = [...path, childIndex];
          const displayIndex = depth === 1
            ? (alphabet[childIndex] || `${childIndex + 1}`)
            : `${displayPrefix}.${childIndex + 1}`;

          result.push({
            ...child,
            rowKey: `child-${parentIndex}-${childPath.join("-")}`,
            _displayIndex: displayIndex,
            _isChild: true,
            _depth: depth,
            _hasChildren: false,
            _parentIdx: parentIndex,
          });

          if (Array.isArray(child.invoiceDetailItems) && child.invoiceDetailItems.length > 0) {
            appendChildren(child.invoiceDetailItems, parentIndex, childPath, depth + 1, displayIndex);
          }
        });
      };

      details.forEach((parent, parentIndex) => {
        const hasChildren =
          Array.isArray(parent.invoiceDetailItems) &&
          parent.invoiceDetailItems.length > 0;

        result.push({
          ...parent,
          rowKey: `parent-${parentIndex}`,
          _displayIndex: `${parentIndex + 1}`,
          _isChild: false,
          _depth: 0,
          _hasChildren: hasChildren,
          _parentIdx: parentIndex,
        });

        if (hasChildren && this.expandedMap[parentIndex]) {
          appendChildren(parent.invoiceDetailItems, parentIndex);
        }
      });

      return result;
    },
  },
  filters: {
    formatDateTime(val) {
      if (!val) return "—";
      return moment(val).format("HH:mm DD/MM/YYYY");
    },
    formatCurrencyLocal(value) {
      const num = Number(value) || 0;
      const formattedValue = new Intl.NumberFormat("de", {
        style: "currency",
        currency: "VND",
      }).format(Math.abs(num));
      return num < 0 ? `(${formattedValue})` : formattedValue;
    }
  },
  methods: {
    formatQuantity(value) {
      return formatQuantityValue(value);
    },

    open(auditId) {
      this.visible = true;
      this.detailData = null;
      this.snapshot = null;
      this.modifiedByName = "";
      this.modifiedDate = "";
      this.actionLabel = "";
      this.expandedMap = {};
      this.fetchData(auditId);
    },
    async fetchData(auditId) {
      this.isLoading = true;
      try {
        const res = await ManagingStudentInvoiceService.getAuditDetail(auditId);
        const data = res?.data?.data;
        if (data) {
          this.detailData = data;
          this.snapshot = data.invoiceSnapshotBefore || data.invoiceSnapshotAfter || {};
          this.modifiedByName = data.modifiedByName || "Hệ thống";
          this.modifiedDate = data.modifiedDate || "";
          this.actionLabel = data.actionLabel || data.actionType || "";
          
          const expanded = {};
          if (this.snapshot && Array.isArray(this.snapshot.invoiceDetails)) {
            this.snapshot.invoiceDetails.forEach((detail, index) => {
              if (
                Array.isArray(detail.invoiceDetailItems) &&
                detail.invoiceDetailItems.length > 0
              ) {
                expanded[index] = true;
              }
            });
          }
          this.expandedMap = expanded;
        }
      } catch (err) {
        console.error(err);
        NotifyMessage.leftCornerNotificationError("Không thể tải chi tiết lịch sử phiếu thu");
      } finally {
        this.isLoading = false;
      }
    },
    toggleExpand(parentIdx) {
      this.$set(this.expandedMap, parentIdx, !this.expandedMap[parentIdx]);
    },
    cellStyleMethod({ row }) {
      if (row._isChild) {
        return { background: "#f8fafc" };
      }
      return {};
    },
    handleClose() {
      this.visible = false;
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";
@import "@/assets/style/sk-table.scss";

/* ─── Table header: xanh primary ──────────────────────── */
/deep/ .custom-header-table.sk-table thead th,
/deep/ .custom-header-table.sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
}
/deep/ .custom-header-table.sk-table thead th > .cell,
/deep/ .custom-header-table.sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
}

.tdi-money--bold {
  font-weight: 700;
  color: #1e293b;
}
.tdi-money--negative {
  color: #dc2626;
  font-weight: 600;
}
.tdi-money--positive {
  color: #16a34a;
  font-weight: 600;
}
.mb-4 {
  margin-bottom: 16px;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.toggle-btn {
  padding: 2px 4px !important;
  font-size: 14px !important;
  color: #2563eb !important;
  min-width: auto !important;
}
.toggle-btn:hover {
  color: #1d4ed8 !important;
}

.parent-name {
  font-weight: 600;
  color: #1e293b;
}
.child-name {
  color: #64748b;
  font-size: 12.5px;
}
.child-index {
  color: #64748b;
  font-style: italic;
}

.text-muted {
  color: #94a3b8;
}
</style>
